import { useState } from 'react';
import { useUsers, useUserMutations } from '../api';
import { useAuth } from '../../store/auth';
import { PageLoader, ErrorState, EmptyState, StatusBadge } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { AdminUser, UserRole } from '../../lib/types';

const ROLES: UserRole[] = ['SuperAdmin', 'Admin', 'Staff'];

const ROLE_TONE: Record<string, string> = {
  SuperAdmin: 'bg-gold-100 text-gold-700',
  Admin: 'bg-blue-100 text-blue-700',
  Staff: 'bg-ink-100 text-ink-600',
};

const emptyForm = {
  name: '',
  email: '',
  password: '',
  role: 'Staff' as UserRole,
  status: 'active' as 'active' | 'disabled',
  permissions: '',
};

export function UsersPage() {
  const me = useAuth((s) => s.user);
  const canWrite = me?.role === 'SuperAdmin';
  const toast = useToast();

  const [roleFilter, setRoleFilter] = useState('');
  const { data, isLoading, error, refetch } = useUsers({ role: roleFilter || undefined });
  const mut = useUserMutations();

  const [modal, setModal] = useState<null | { mode: 'create' | 'edit'; row?: AdminUser }>(null);
  const [form, setForm] = useState(emptyForm);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [removing, setRemoving] = useState<AdminUser | null>(null);

  const rows = data?.data ?? [];

  const openCreate = () => {
    setForm(emptyForm);
    setModal({ mode: 'create' });
  };
  const openEdit = (row: AdminUser) => {
    setForm({
      name: row.name,
      email: row.email,
      password: '',
      role: row.role,
      status: row.status,
      permissions: row.permissions.join(', '),
    });
    setModal({ mode: 'edit', row });
  };

  const save = async () => {
    const permissions = form.permissions
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    try {
      if (modal?.mode === 'create') {
        await mut.create.mutateAsync({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          status: form.status,
          permissions,
        });
        toast('User created', 'success');
      } else if (modal?.row) {
        await mut.update.mutateAsync({
          id: modal.row.id,
          body: { name: form.name, role: form.role, status: form.status, permissions },
        });
        toast('User updated', 'success');
      }
      setModal(null);
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Save failed', 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Users</h1>
          <p className="text-sm text-ink-400">
            The admin team. {canWrite ? 'You can add and manage accounts.' : 'Read-only — only a SuperAdmin can make changes.'}
          </p>
        </div>
        {canWrite && (
          <button className="btn-primary" onClick={openCreate}>
            + New user
          </button>
        )}
      </div>

      <div className="card flex flex-wrap gap-2 p-3">
        <select className="input w-auto" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No users" />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Last login</th>
                  {canWrite && <th className="px-3 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((u) => (
                  <tr key={u.id} className="hover:bg-ink-50/60">
                    <td className="px-3 py-2 font-medium">
                      {u.name}
                      {u.id === me?.id && <span className="ml-2 text-xs text-ink-400">(you)</span>}
                    </td>
                    <td className="px-3 py-2 text-ink-600">{u.email}</td>
                    <td className="px-3 py-2">
                      <span className={`badge ${ROLE_TONE[u.role] ?? 'bg-ink-100 text-ink-600'}`}>{u.role}</span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={u.status === 'active' ? 'active' : 'disabled'} />
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-400">
                      {u.lastLoginAt ? dateTime(u.lastLoginAt) : 'never'}
                    </td>
                    {canWrite && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost px-2 py-1" onClick={() => openEdit(u)}>
                            Edit
                          </button>
                          <button
                            className="btn-ghost px-2 py-1"
                            onClick={() => {
                              setResetPw('');
                              setResetting(u);
                            }}
                          >
                            Reset PW
                          </button>
                          <button
                            className="btn-ghost px-2 py-1 text-red-600 hover:bg-red-50"
                            disabled={u.id === me?.id}
                            onClick={() => setRemoving(u)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / edit */}
      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'New user' : `Edit ${modal?.row?.name ?? ''}`}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setModal(null)}>
              Cancel
            </button>
            <button className="btn-primary" disabled={mut.create.isPending || mut.update.isPending} onClick={save}>
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="label">Name</span>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="col-span-2 block">
            <span className="label">Email</span>
            <input
              className="input"
              type="email"
              value={form.email}
              disabled={modal?.mode === 'edit'}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          {modal?.mode === 'create' && (
            <label className="col-span-2 block">
              <span className="label">Temporary password</span>
              <input
                className="input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 chars, upper + lower + digit"
              />
            </label>
          )}
          <label className="block">
            <span className="label">Role</span>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}>
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Status</span>
            <select
              className="input"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'disabled' })}
            >
              <option value="active">active</option>
              <option value="disabled">disabled</option>
            </select>
          </label>
          <label className="col-span-2 block">
            <span className="label">Permissions (comma-separated, optional)</span>
            <input
              className="input"
              value={form.permissions}
              onChange={(e) => setForm({ ...form, permissions: e.target.value })}
              placeholder="e.g. products:write, bookings:read"
            />
          </label>
        </div>
      </Modal>

      {/* Reset password */}
      <Modal
        open={!!resetting}
        onClose={() => setResetting(null)}
        title={`Reset password — ${resetting?.name ?? ''}`}
        size="sm"
        footer={
          <>
            <button className="btn-ghost" onClick={() => setResetting(null)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={mut.resetPassword.isPending || resetPw.length < 8}
              onClick={async () => {
                try {
                  await mut.resetPassword.mutateAsync({ id: resetting!.id, newPassword: resetPw });
                  toast('Password reset — the user must sign in again', 'success');
                  setResetting(null);
                } catch (e) {
                  toast(e instanceof ApiRequestError ? e.message : 'Failed', 'error');
                }
              }}
            >
              Reset
            </button>
          </>
        }
      >
        <label className="block">
          <span className="label">New password</span>
          <input
            className="input"
            value={resetPw}
            onChange={(e) => setResetPw(e.target.value)}
            placeholder="Min 8 chars, upper + lower + digit"
          />
        </label>
      </Modal>

      <ConfirmDialog
        open={!!removing}
        title="Delete user?"
        tone="danger"
        confirmLabel="Delete"
        busy={mut.remove.isPending}
        message={
          removing && (
            <p>
              Permanently remove <b>{removing.name}</b> ({removing.email})? Their audit history stays. This can't be undone.
            </p>
          )
        }
        onCancel={() => setRemoving(null)}
        onConfirm={async () => {
          try {
            await mut.remove.mutateAsync(removing!.id);
            toast('User deleted', 'success');
            setRemoving(null);
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Delete failed', 'error');
            setRemoving(null);
          }
        }}
      />
    </div>
  );
}
